/**
 * Session API service
 * Handles all session-related API calls
 */

import apiClient from '../utils/apiClient';
import {
  AuditFieldInput,
  Session,
  SessionListDTO,
  SessionNote,
} from '../store/sessionSlice';
import {
  SessionMergeJob,
  SessionMergePreview,
  SessionMergeRequest,
  SessionMergeStart,
} from '../types/sessionMerge';

/** Query params asking a list endpoint to include archived (merged away) sessions. */
const includeArchivedToParams = (includeArchived: boolean): Record<string, string> =>
  includeArchived ? { include_archived: 'true' } : {};

export const sessionService = {
  /**
   * Fetch all sessions for the current user
   */
  async fetchSessions(includeArchived = false): Promise<SessionListDTO[]> {
    const response = await apiClient.get('/api/sessions', {
      params: includeArchivedToParams(includeArchived),
    });
    return response.data;
  },

  /**
   * Fetch ALL sessions across all users with pagination (requires admin/engineer access)
   * Returns one page of sessions plus a next_token for fetching the next page.
   * Call repeatedly with next_token until it's null to load all sessions.
   */
  async fetchAllSessions(nextToken?: string | null, includeArchived = false): Promise<{
    sessions: SessionListDTO[];
    next_token: string | null;
  }> {
    const params: Record<string, string> = {
      page_size: '500',
      ...includeArchivedToParams(includeArchived),
    };
    if (nextToken) {
      params.next_token = nextToken;
    }
    const response = await apiClient.get('/api/sessions/all', { params });
    return response.data;
  },

  /**
   * Fetch a single session by ID
   */
  async fetchSession(sessionId: string): Promise<Session> {
    const response = await apiClient.get(`/api/sessions/${sessionId}`);
    return response.data;
  },

  /**
   * Fetch lightweight session status (for polling)
   * Returns only status + updated_at to minimize data transfer
   */
  async fetchSessionStatus(sessionId: string): Promise<{
    session_id: string;
    status: string;
    running_tasks: string[];
    updated_at: string;
  }> {
    const response = await apiClient.get(`/api/sessions/${sessionId}/status`);
    return response.data;
  },

  async fetchTranscript(sessionId: string): Promise<{
    session_id: string;
    full_text: string;
    segments: Array<{
      transcript_id: string;
      text: string;
      speaker: string | null;
      start_time: number;
      end_time: number;
      created_at: string;
      type: string;
    }>;
    segment_count: number;
    transcripts: Array<{
      transcript_id: string;
      audio_id: string;
      created_by?: string | null;
      status: 'processing' | 'postprocessing' | 'completed' | 'postprocess_failed';
      created_at: string;
      completed_at: string | null;
      evidence?: Record<string, string[]>;
    }>;
  }> {
    const response = await apiClient.get(`/api/sessions/${sessionId}/transcript`);
    return response.data;
  },

  /**
   * Update extracted fields for a session
   */
  async updateFields(sessionId: string, fields: Record<string, string | string[] | Record<string, string[]>>): Promise<Session> {
    const response = await apiClient.put(`/api/sessions/${sessionId}/fields`, fields);
    return response.data;
  },

  /**
   * Record a validator's review of fields. Locks the reviewed values into
   * human_overrides_fields under a USER_VALIDATION source and advances validation_state.
   */
  async validateFields(
    sessionId: string,
    fields: Record<string, string | string[] | Record<string, string[]>>,
    comments?: Record<string, string>,
  ): Promise<Session> {
    const response = await apiClient.put(`/api/sessions/${sessionId}/validation`, { fields, comments: comments || {} });
    return response.data;
  },

  /**
   * Fetch the validated sessions an auditor can audit, across all users and sites (requires the
   * auditor group). Each DTO carries audit_state, so callers narrow by audit stage themselves.
   */
  async fetchValidatedSessions(): Promise<SessionListDTO[]> {
    const response = await apiClient.get('/api/sessions/validated');
    return response.data;
  },

  /**
   * Record an auditor's results on a validated session's fields. Writes to audited_fields and
   * audit_state only — never to the field stores or edit_history — so an audit cannot change
   * what the reporting user sees or what the AUSTIN backfill submits.
   */
  async auditFields(
    sessionId: string,
    fields: Record<string, AuditFieldInput>,
  ): Promise<Session> {
    const response = await apiClient.put(`/api/sessions/${sessionId}/audit`, { fields });
    return response.data;
  },

  /**
   * Update group_id (site code) for a session
   */
  async updateGroupId(sessionId: string, groupId: string): Promise<{
    session_id: string;
    group_id: string;
    updated_at: string;
  }> {
    const response = await apiClient.put(`/api/sessions/${sessionId}/group`, { group_id: groupId });
    return response.data;
  },

  /**
   * Generate summary for a session
   */
  async generateSummary(sessionId: string): Promise<{
    session_id: string;
    summary: string;
    generated_at: string;
  }> {
    const response = await apiClient.post(`/api/sessions/${sessionId}/summary`);
    return response.data;
  },

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/api/sessions/${sessionId}`);
  },

  /**
   * Delete a single transcript (consent-rejection flow — erases the transcript
   * captured during the consent window).
   */
  async deleteTranscript(sessionId: string, transcriptId: string): Promise<void> {
    await apiClient.delete(`/api/sessions/${sessionId}/transcripts/${transcriptId}`);
  },

  /**
   * Create or update a session note
   */
  async saveSessionNote(
    sessionId: string,
    content: string,
    noteId?: string
  ): Promise<{ note: SessionNote; reanalysis_triggered: boolean }> {
    const response = await apiClient.post(
      `/api/sessions/${sessionId}/notes`,
      { content, ...(noteId && { note_id: noteId }) }
    );
    return response.data;
  },

  /**
   * Update session metadata (e.g., case_id)
   */
  async updateMetadata(
    sessionId: string,
    metadata: Record<string, any>
  ): Promise<{
    session_id: string;
    metadata: Record<string, any>;
    updated_at: string;
  }> {
    const response = await apiClient.put(`/api/sessions/${sessionId}/metadata`, metadata);
    return response.data;
  },

  /**
   * Append client-reported UX latency datapoints (backfill / validation). Best-effort
   * telemetry: send only the new datapoint(s); the server appends via list_append.
   */
  async appendMetrics(
    sessionId: string,
    events: {
      backfill_latency?: { field_count: number; latency_ms: number; timestamp: string }[];
      validation_latency?: { field_name: string; latency_ms: number; timestamp: string }[];
    },
  ): Promise<void> {
    await apiClient.put(`/api/sessions/${sessionId}/metrics`, events);
  },

  /**
   * Check whether two sessions can be merged (admin only): per-session and pair blockers,
   * the fields set by hand that differ, and whether the recordings overlap in time.
   */
  async previewSessionMerge(sessionIds: string[]): Promise<SessionMergePreview> {
    const response = await apiClient.post('/api/sessions/merge/preview', { session_ids: sessionIds });
    return response.data;
  },

  /**
   * Start merging two sessions (admin only). Returns once the merge job is accepted; poll
   * fetchSessionMergeJob for completion. Retrying the same pair after a failure resumes it.
   */
  async startSessionMerge(request: SessionMergeRequest): Promise<SessionMergeStart> {
    const response = await apiClient.post('/api/sessions/merge', request);
    return response.data;
  },

  /**
   * Fetch the status of a merge job by the merged session's ID.
   */
  async fetchSessionMergeJob(mergedSessionId: string): Promise<SessionMergeJob> {
    const response = await apiClient.get(`/api/sessions/merge/${mergedSessionId}`);
    return response.data;
  },
};
